<template>
    <md-layout class="ql-project-list-view">
        <div class="ql-project-list-view-toolbar">
            <div class="list-view-split-bar" v-if="splitView">
                <!-- Split view's own layer picker (Josh, 21 Oct 2026). Native markup, no md-* component. -->
                <div class="ql-split-layers" ref="splitLayers">
                    <button type="button" class="ql-split-layers-btn" :class="{ 'is-open': splitLayersOpen, 'is-filtered': splitLayersLabel !== 'All' }" @click="splitLayersOpen = !splitLayersOpen" aria-haspopup="true" :aria-expanded="splitLayersOpen ? 'true' : 'false'">
                        Layers: {{ splitLayersLabel }}<span class="ql-split-layers-caret"></span>
                    </button>
                    <div class="ql-split-layers-menu" v-if="splitLayersOpen">
                        <label class="ql-split-layer-option" v-for="layer in layers" :key="layer.id">
                            <input type="checkbox" :checked="visibleSplitLayerIDs.includes(layer.id)" @change="e => changeSplitLayer(layer.id, e.target.checked)">
                            <span class="ql-split-layer-swatch" :style="{ background: layer.color }"></span>
                            <span class="ql-split-layer-name">{{ layer.title }}</span>
                        </label>
                        <div class="ql-split-layers-foot">
                            <button type="button" @click="showAllSplitLayers">Show all</button>
                        </div>
                    </div>
                </div>
                <label class="ql-split-page-sync" v-if="projectTotalPages > 1">
                    <input type="checkbox" v-model="localPageSync"> List Sync
                </label>
                <button class="ql-split-close" @click="closeSplitView" title="Close list view" aria-label="Close list view">&times;</button>
            </div>
            <div class="list-view-visible-layers" v-if="!splitView">
                <strong style="margin-right: 1rem;">Visible Layers:</strong>
                <md-checkbox
                    v-for="layer in layers"
                    :key="layer.id"
                    :value="visibleListLayerIDs.includes(layer.id)"
                    @change="val => changeVisibleListLayer(layer.id, val)"
                    style="margin-right: 1rem;"
                >{{ layer.title }}</md-checkbox>
            </div>
            <div class="list-view-auto-number" v-if="!splitView">
                <md-button class="md-raised" @click="openAutoNumberModal" :disabled="!selectedCueIds.length || numberingInProgress || deletingInProgress">
                    <span v-if="numberingInProgress">Numbering...</span>
                    <span v-else>Auto Number Selected Cues</span>
                </md-button>
                <md-button class="md-raised" @click="openDeleteSelected" :disabled="!selectedCueIds.length || numberingInProgress || deletingInProgress">
                    <span v-if="deletingInProgress">Deleting...</span>
                    <span v-else>Delete Selected Cues</span>
                </md-button>
            </div>
            <div class="list-view-export" v-if="!splitView">
                <strong>Download all visible layers as a CSV file:&nbsp;</strong>
                <md-button class="md-raised" @click="exportList" :disabled="exporting">
                    <span v-if="exporting">Exporting...</span>
                    <span v-else>Export</span>
                </md-button>
                <!-- md-button exception approved by Josh so this matches the Export button. -->
                <md-button class="md-raised" @click="openEosModal">Export for Eos</md-button>
            </div>
        </div>
        <!-- Native markup, no md-* component: this is new UI and vue-material has
             no Vue 3 release (see the migration guardrail). Sits outside the
             toolbar so it does not disturb its space-between flex row. -->
        <div class="ql-structure-sort-hint" v-if="structureHiddenBySort && !splitView">
            Acts, Scenes, and Songs show only when sorted by page in ascending order.
        </div>
        <auto-number-cues ref="autoNumberCues" @auto-number="onAutoNumber" />
        <md-dialog ref="autoNumberOverwriteDialog">
            <md-dialog-content>{{ overwriteCount }} of the cues you have chosen to auto number already have a number. These cue numbers will be over-written by the auto number function. This cannot be undone.</md-dialog-content>
            <md-dialog-actions>
                <md-button @click="cancelOverwrite">Cancel</md-button>
                <md-button class="md-primary" @click="confirmOverwrite">Auto Number</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog ref="autoNumberResultDialog">
            <md-dialog-content>{{ resultMessage }}</md-dialog-content>
            <md-dialog-actions>
                <md-button v-if="lastNumbered.length" @click="undoAutoNumber">Undo</md-button>
                <md-button class="md-primary" @click="closeResultDialog">Done</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-dialog ref="deleteSelectedDialog">
            <md-dialog-content>Delete {{ selectedCueIds.length }} selected {{ selectedCueIds.length === 1 ? 'cue' : 'cues' }}? This cannot be undone.</md-dialog-content>
            <md-dialog-actions>
                <md-button @click="cancelDeleteSelected">Cancel</md-button>
                <md-button class="md-primary" @click="confirmDeleteSelected">Delete</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-layout class="ql-project-list-view-content" :class="{ 'ql-split-list': splitView }" ref="ql-project-list-view-content">
            <table class="ql-project-list-view-table">
                <thead class="ql-project-list-view-table-fixed-header">
                    <tr>
                        <th class="ql-cell-empty ql-cell-select"><md-checkbox :value="allSelected" @change="toggleSelectAll">All</md-checkbox></th>
                        <th class="ql-cell-empty"></th>
                        <th class="ql-cell-sortable" :class="{ 'ql-sort-active': sortBy === 'page' && !splitView }" @click="toggleSortBy('page')">Page <md-icon v-show="sortBy === 'page' && sortAsc">arrow_drop_down</md-icon><md-icon v-show="sortBy === 'page' && !sortAsc">arrow_drop_up</md-icon><md-tooltip v-if="!splitView">Sort by page</md-tooltip></th>
                        <th></th>
                        <th class="ql-cell-sortable" :class="{ 'ql-sort-active': sortBy === 'layer' && !splitView }" @click="toggleSortBy('layer')">Layer <md-icon v-show="sortBy === 'layer' && sortAsc">arrow_drop_down</md-icon><md-icon v-show="sortBy === 'layer' && !sortAsc">arrow_drop_up</md-icon><md-tooltip v-if="!splitView">Sort by layer</md-tooltip></th>
                        <th class="ql-cell-sortable" :class="{ 'ql-sort-active': sortBy === 'cue' && !splitView }" @click="toggleSortBy('cue')">Cue <md-icon v-show="sortBy === 'cue' && sortAsc">arrow_drop_down</md-icon><md-icon v-show="sortBy === 'cue' && !sortAsc">arrow_drop_up</md-icon><md-tooltip v-if="!splitView">Sort by cue number</md-tooltip></th>
                        <th v-for="attr in availableColumns" v-if="(!['user1', 'user2', 'user3' , 'user4', 'workNote'].includes(attr))" :class="'ql-cell-attr-' + attr" :key="attr">{{attributes[attr]}}</th>
                        <th v-for="attr in availableColumns" v-if="('workNote' === attr)" :class="['ql-cell-attr-' + attr, { 'ql-cell-attr-workNote--noteslist': notesListLinked }]" :key="attr">{{ notesListLinked ? 'Notes List' : attributes[attr] }}</th>
                        <th v-for="(key, udIndex) in getAllUserDefinedKeys()" :key="udIndex + '_' + key" :class="'ql-cell-attr-' + key">
                            {{ key }}
                        </th>
                    </tr>
                </thead>
                <tbody v-if="topSpacerHeight > 0" class="ql-vscroll-spacer"><tr><td colspan="100" :style="{ height: topSpacerHeight + 'px', padding: 0, border: 0 }"></td></tr></tbody>
                <template v-for="item in windowedRows">
                    <project-list-view-structure-header-row
                        v-if="item.row.kind === 'header'"
                        :key="item.row.key"
                        :marker="item.row.marker"
                        :empty="item.row.isEmpty"
                        :splitView="splitView"
                        @switchViewToPage="switchViewToPage"
                    />
                    <project-list-view-cue-row
                        v-else
                        :key="item.row.cue.id"
                        :cue="item.row.cue"
                        :cueIndex="item.row.cueIndex"
                        :layerUserDefined="layerUserDefined"
                        :getAllUserDefinedKeys="getAllUserDefinedKeys"
                        :getUserDefinedValue="getUserDefinedValue"
                        :editable="isEditableCue(item.row.cue)"
                        :selected="isCueSelected(item.row.cue.id)"
                        :availableColumns="availableColumns"
                        :notesListColumn="notesListColumn"
                        :splitView="splitView"
                        @toggleSelect="toggleSelectCue"
                        @switchViewToPage="switchViewToPage"
                        @cellNavigate="onCellNavigate"
                    />
                </template>
                <tbody v-if="bottomSpacerHeight > 0" class="ql-vscroll-spacer"><tr><td colspan="100" :style="{ height: bottomSpacerHeight + 'px', padding: 0, border: 0 }"></td></tr></tbody>
            </table>
            <div v-if="splitView && allCues.length === 0" class="ql-split-empty">
                No layers shown in this list.
            </div>
        </md-layout>
        <div v-if="flashMsg" class="ql-list-view-flash">{{ flashMsg }}</div>
        <div v-if="eosModalOpen" class="ql-eos-modal-overlay" @click.self="cancelEosModal">
            <div class="ql-eos-modal">
                <div class="ql-eos-modal-title">Export for ETC Eos</div>
                <p class="ql-eos-modal-desc">Cues with no time will be given this default time.</p>
                <label class="ql-eos-modal-label">Default Cue Time
                    <input type="number" step="0.1" min="0" v-model="eosDefaultTime" class="ql-eos-modal-input" @keyup.enter="confirmEosExport">
                </label>
                <div class="ql-eos-modal-actions">
                    <button type="button" class="ql-eos-btn" @click="cancelEosModal">Cancel</button>
                    <button type="button" class="ql-eos-btn ql-eos-btn-primary" @click="confirmEosExport" :disabled="eosExporting">{{ eosExporting ? 'Exporting…' : 'Export' }}</button>
                </div>
            </div>
        </div>
    </md-layout>
</template>

<script>
import { mapGetters } from 'vuex';

import Utils from '@/lib/Utils';
import { NOTESLIST_INTEGRATION_ENABLED } from '@/lib/URLs';
import { anyLiveWorkNote, notesListColumnWidth } from '@/lib/noteslist-column';

import ProjectListViewCueRow from '@/components/project/list-view/cell-row';
import AutoNumberCues from '@/modals/auto-number-cues';
import * as CellNav from '@/lib/listViewCellNav';
import { buildEosRows, toEosCsv } from '@/lib/eosExport';
import { pageForExport } from '@/lib/cue-page';
import { assignSections, buildStructureRows, compareDocOrder, innermostSceneLabel, sectionCellsForExport } from '@/lib/structureOrder';
import { sidebarMarkerRowText } from '@/lib/marker-labels';
import { splitLayersLabel, toggledSplitLayerIDs } from '@/lib/split-layers';
import StructureHeaderRow from '@/components/project/list-view/structure-header-row';

// The sticky header is now native CSS (thead th { position: sticky; top: 0 }),
// which also lets columns freeze on the X axis (a transformed ancestor would
// have broken sticky-left). No JS scroll-transform needed.

// Stage A: how far above/below the list's scroll viewport (px) to keep rows
// mounted. Generous so scrolling never reveals an unmounted row — and so rows
// ahead of the viewport get measured (measureRenderedRows) before the user
// reaches them, which keeps fast scrolling through part cues from flashing.
const LIST_VIRT_OVERSCAN = 1000;

// Height of a Structure section header row. Must match $ql-structure-row-h in
// list-view/structure-header-row.vue, or rowOffsets drifts from what renders.
// Recalibrated from the DOM in updateListViewport, so a zoom change self-corrects.
const STRUCTURE_ROW_H = 28;

const ProjectListView = {
    name: 'ProjectListView',
    props: [
        'currentPage',
        'splitView',
        'pageSync'
    ],
    data: function() {
        return {
            cueAttributes: {...Utils.cueAttributes, parts_id: 'P', parts_label: 'P Label'},
            attributes: {...Utils.cueAttributes, parts_id: 'P', parts_label: 'P Label'},
            sortBy: 'page',
            sortAsc: true,
            handler: 0,
            // Stage A: list-view row virtualization (scroll window).
            listVTop: 0,
            listVHeight: 800,
            listRowH: 30,
            // Measured rendered heights, keyed by `${cueId}|${parts}` so a parts
            // change re-measures. Makes rowOffsets exact for scrolled-through rows;
            // the part-count estimate is only a fallback for not-yet-rendered rows.
            measuredHeights: {},
            // Live height of a structure header row (see STRUCTURE_ROW_H).
            structureRowH: STRUCTURE_ROW_H,
            // Spreadsheet range selection (Shift+Arrow). Coords are {r, c} in the
            // listViewCellNav data-attribute space; null when no range is active.
            rangeAnchor: null,
            rangeFocus: null,
            // The cell the user is on, tracked by coordinate so its highlight
            // survives virtualization re-render/scroll (DOM :focus is lost when the
            // row unmounts). {r, c} or null.
            activeCell: null,
            flashMsg: '',
            flashTimer: null,
            // Set only for the duration of a bulk paste: forces the virtualizer to
            // keep rows mounted as far down as the paste reaches. See applyBlock.
            pasteMountEnd: null,
            splitLayersOpen: false,
            eosModalOpen: false,
            eosDefaultTime: '2.9',
            eosExporting: false,
            exporting: false,
            selectedCueIds: [],
            numberingInProgress: false,
            deletingInProgress: false,
            overwriteCount: 0,
            pendingAutoNumber: null,
            lastNumbered: [],
            resultMessage: '',
            userDefined: {
                user1: { label: 'User 1', value: false },
                user2: { label: 'User 2', value: false },
                user3: { label: 'User 3', value: false },
                user4: { label: 'User 4', value: false }
            }
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'layers',
            'layerByCue',
            'cues',
            'allVisibleListCues',
            'userProjectByUidData',
            'visibleListLayers',
            'visibleListLayerIDs',
            'visibleSplitLayers',
            'visibleSplitLayerIDs',
            'allVisibleSplitCues',
            'editableLayers',
            'projectTotalPages',
            'splitPageSync',
            'scriptMarkersNav'
        ]),
        // In split view the list filters by its own choice (layers.splitViewing), which follows
        // the PDF's visible layers (layers.viewing) until the person first uses the split
        // picker. The full List view uses layers.listViewing.
        effectiveVisibleLayers: function() {
            return this.splitView ? this.visibleSplitLayers : this.visibleListLayers;
        },
        effectiveVisibleListCues: function() {
            return this.splitView ? this.allVisibleSplitCues : this.allVisibleListCues;
        },
        // "All", or "3 of 4": shown layers out of the show's layers (deleted ids already dropped).
        splitLayersLabel: function() {
            return splitLayersLabel(this.visibleSplitLayerIDs.length, this.layers.length);
        },
        localPageSync: {
            get: function() {
                return this.splitPageSync === 'true';
            },
            set: function(val) {
                this.$store.dispatch('setSplitPageSync', { value: val ? 'true' : 'false' });
            }
        },
        selectableCueIds: function() {
            return this.allCues.filter(cue => this.isEditableCue(cue)).map(cue => cue.id);
        },
        allSelected: function() {
            const ids = this.selectableCueIds;
            return ids.length > 0 && ids.every(id => this.selectedCueIds.includes(id));
        },
        columnsAvailability: function() {
            return {
                ...Object.keys(this.attributes).reduce((attrs, attr) => ({...attrs, [attr]: this.effectiveVisibleLayers.some(layer => layer.attributes[attr])}), {}),
                workNote: true,
                label: true,
                user1: true,
                user2: true,
                user3: true,
                user4: true
            };
        },
        availableColumns: function() {
            const columns = Object.keys(this.columnsAvailability).filter(attr => this.columnsAvailability[attr]);
            const partsIndex = columns.indexOf('parts');
            if (partsIndex > -1) {
                columns.splice(partsIndex + 1, 0, 'parts_id', 'parts_label');
            }
            return columns;
        },
        // Recompute pinned column widths when the column set or cue count changes
        // (not on every keystroke — same-length edits keep the existing widths).
        columnWidthSignature() {
            return this.availableColumns.join(',') + '|' + this.allCues.length + '|' + this.getAllUserDefinedKeys().join(',')
                + '|' + this.notesListLinked + '|' + this.notesListAnyLiveWorkNote;
        },
        // On a show linked to The Notes List the Work Note column is the Notes List column (Josh, 9 Oct 2026):
        // the Add Note button and open count on every row, plus any Cuelist work note still waiting to be archived.
        notesListLinked() {
            return !!(NOTESLIST_INTEGRATION_ENABLED
                && this.project
                && this.project.noteslist_link_status === 'linked'
                && this.project.noteslist_production_id);
        },
        // Once, for the whole list: decides whether the column narrows to the button and count.
        notesListAnyLiveWorkNote() {
            return this.notesListLinked && anyLiveWorkNote(this.allCues);
        },
        notesListColumn() {
            return this.notesListLinked ? { linked: true, narrow: !this.notesListAnyLiveWorkNote } : null;
        },
        layerUserDefined() {
            return this.effectiveVisibleLayers.map((layer) => {
                const userDefinedObj = {};
                
                for (const [key, value] of Object.entries(layer.userDefined || {})) {
                    if (key && value.value === true) {
                        if (value.label === '') {
                            userDefinedObj[key] = `${layer.title}: ${(key.charAt(0).toUpperCase() + key.slice(1))}`;
                        } else {
                            userDefinedObj[key] = `${layer.title}: ${value.label}`;
                        }
                    }
                }

                return {
                    id: layer.id,
                    userDefined: userDefinedObj
                };
            });
        },
        layerNotUserDefined: function() {
            const notLayer = this.layers.flatMap(obj =>
                obj.userDefined && Object.keys(obj.userDefined).filter(key => !obj.userDefined[key].value)
            );
            const difference = notLayer.filter(item => !this.layerUserDefined.includes(item));
            return [...new Set(difference)];
        },
        // Structure markers in document order. One place decides, so the rows
        // and both exports cannot disagree.
        sortedMarkers: function() {
            return this.scriptMarkersNav || [];
        },
        // Section headers only tell the truth in document order. Split view is
        // hard-locked to it, and in the full list that is Page ascending (which
        // this flag redefines to page -> y -> x). Page-descending would put a
        // section bar above cues running backwards, and Layer/Cue are global
        // orderings that sectioning would shatter — so headers hide in all three.
        showStructureHeaders: function() {
            if (!this.sortedMarkers.length) return false;
            if (this.splitView) return true;
            return this.sortBy === 'page' && this.sortAsc;
        },
        // Structure exists but the current sort is hiding it — drives the hint.
        structureHiddenBySort: function() {
            if (!(this.scriptMarkersNav || []).length) return false;
            return !this.showStructureHeaders;
        },
        // The rendered row set: cues with section headers interleaved. `cueIndex`
        // remains an index into allCues, so data-ql-r coordinates and every
        // existing consumer of allCues are untouched. With the flag off (or no
        // markers) this is a 1:1 map and every emitted node is what shipped before.
        listRows: function() {
            if (!this.showStructureHeaders) {
                return this.allCues.map((cue, i) => ({ kind: 'cue', cue: cue, cueIndex: i }));
            }
            return buildStructureRows(this.allCues, this.sortedMarkers);
        },
        // allCues index -> listRows index. The two spaces diverge the moment a
        // header exists, and scrollListToPage crosses between them.
        cueIndexToRowIndex: function() {
            const map = new Array(this.allCues.length);
            const rows = this.listRows;
            for (let i = 0; i < rows.length; i += 1) {
                if (rows[i].kind === 'cue') {
                    map[rows[i].cueIndex] = i;
                }
            }
            return map;
        },
        // Stage A: the row window derived from scroll position + measured row
        // height. Rows outside it are not mounted (only spacers hold the height),
        // so entering/leaving list view and scrolling stay cheap at high cue counts.
        // Cumulative pixel offset of each ROW (variable height: a part cue renders
        // `data.parts` rows, so it's parts * a single row's height; a structure
        // header is a fixed structureRowH). rowOffsets[i] is the top of row i;
        // rowOffsets[len] is the total content height. Recomputed only when the row
        // set or a measured height changes (cached computed), not on every scroll —
        // so it stays cheap at thousands of cues.
        rowOffsets: function() {
            const rowH = this.listRowH || 30;
            const hdrH = this.structureRowH || STRUCTURE_ROW_H;
            const rows = this.listRows;
            const offs = new Array(rows.length + 1);
            offs[0] = 0;
            for (let i = 0; i < rows.length; i += 1) {
                let h = hdrH;
                if (rows[i].kind === 'cue') {
                    const cue = rows[i].cue;
                    const parts = Math.max(1, Number(cue.data && cue.data.parts) || 1);
                    const measured = this.measuredHeights[`${cue.id}|${parts}`];
                    h = measured > 0 ? measured : (parts * rowH);
                }
                offs[i + 1] = offs[i] + h;
            }
            return offs;
        },
        listWindow: function() {
            const offs = this.rowOffsets;
            const len = this.listRows.length;
            const top = this.listVTop - LIST_VIRT_OVERSCAN;
            const bottom = this.listVTop + this.listVHeight + LIST_VIRT_OVERSCAN;
            // start = the row whose band contains `top` (first offs > top, minus 1).
            let start = this.upperBoundOffset(offs, top) - 1;
            if (start < 0) start = 0;
            // end = one past the row whose band contains `bottom`.
            let end = this.upperBoundOffset(offs, bottom);
            if (end > len) end = len;
            if (end < start) end = start;
            // A bulk paste widens the window so its DOM walk can reach the rows it
            // is about to write to. Cleared as soon as the paste is applied.
            if (this.pasteMountEnd != null && this.pasteMountEnd > end) {
                end = Math.min(len, this.pasteMountEnd);
            }
            return { start, end };
        },
        windowedRows: function() {
            const { start, end } = this.listWindow;
            const rows = this.listRows;
            const out = [];
            for (let i = start; i < end; i += 1) {
                out.push({ row: rows[i], index: i });
            }
            return out;
        },
        topSpacerHeight: function() {
            return this.rowOffsets[this.listWindow.start] || 0;
        },
        bottomSpacerHeight: function() {
            const offs = this.rowOffsets;
            return Math.max(0, offs[offs.length - 1] - offs[this.listWindow.end]);
        },
        allCues: function() {
            const shaped = this.effectiveVisibleListCues.filter(c => this.effectiveVisibleLayers.some(l => l.id === c.layer)).map(c => ({...c, title: c.title.trim(), layer: this.layerByCue(c)}));
            if (this.splitView) {
                // Split view mirrors the PDF: lock the order to page -> y -> x
                // (reading order: top-to-bottom, then left-to-right) so the list
                // always lines up with the PDF layout. Column-header sorting is
                // disabled in split (see toggleSortBy) — the same page->y->x order
                // used by auto-numbering.
                // Through the shared comparator so the rows and the section
                // assignment agree on legacy pixel coordinates.
                return shaped.sort((a, b) => compareDocOrder({ item: a, isMarker: false }, { item: b, isMarker: false }));
            }
            if (this.sortBy === 'page' || this.sortBy === 'layer') {
                // Document-order sorts. These were behind the structureInList flag
                // until it came off; they are now how Page and Layer always sort.
                //
                // PAGE becomes true document order (page -> down the page ->
                // across), matching split view and Auto Number. Auto Number already
                // assigns numbers in that order, so today the list disagrees with
                // the numbering it just produced; this makes them agree.
                //
                // LAYER stays GLOBAL — all of one layer in a single run, which is
                // the point of the sort — but orders by script position within the
                // layer instead of by cue number.
                const dir = this.sortAsc ? 1 : -1;
                const by = this.sortBy;
                return shaped.sort((a, b) => {
                    if (by === 'layer') {
                        const at = a.layer.title.toUpperCase();
                        const bt = b.layer.title.toUpperCase();
                        if (at !== bt) {
                            return at < bt ? -dir : dir;
                        }
                    }
                    return dir * compareDocOrder({ item: a, isMarker: false }, { item: b, isMarker: false });
                });
            }
            return shaped.sort((a, b) => {
                if (this.sortBy) {
                    if (this.sortBy === 'page') {
                        if (this.sortAsc) {
                            if (a.pageNumber > b.pageNumber) return 1;
                            if (a.pageNumber < b.pageNumber) return -1;

                            const aLayerTitle = a.layer.title.toUpperCase();
                            const bLayerTitle = b.layer.title.toUpperCase();
                            if (aLayerTitle < bLayerTitle) return -1;
                            if (aLayerTitle > bLayerTitle) return 1;

                            if (a.title === '' && b.title === '') {
                                return a.y - b.y;
                            } else if (!a.title) {
                                return -1;
                            } else if (!b.title) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                                const aTitle = a.title.toUpperCase();
                                const bTitle = b.title.toUpperCase();
                                if (aTitle < bTitle) return -1;
                                if (aTitle > bTitle) return 1;
                                return 0;
                            } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                                return Number.parseFloat(a.title) - Number.parseFloat(b.title);
                            } else if (Number.isNaN(Number.parseFloat(a.title))) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(b.title))) {
                                return -1;
                            }
                        }

                        if (a.pageNumber > b.pageNumber) return -1;
                        if (a.pageNumber < b.pageNumber) return 1;

                        const aLayerTitle = a.layer.title.toUpperCase();
                        const bLayerTitle = b.layer.title.toUpperCase();
                        if (aLayerTitle < bLayerTitle) return 1;
                        if (aLayerTitle > bLayerTitle) return -1;

                        if (a.title === '' && b.title === '') {
                            return b.y - a.y;
                        } else if (!a.title) {
                            return 1;
                        } else if (!b.title) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                            const aTitle = a.title.toUpperCase();
                            const bTitle = b.title.toUpperCase();
                            if (aTitle < bTitle) return 1;
                            if (aTitle > bTitle) return -1;
                            return 0;
                        } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                            return Number.parseFloat(b.title) - Number.parseFloat(a.title);
                        } else if (Number.isNaN(Number.parseFloat(a.title))) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(b.title))) {
                            return 1;
                        }
                    }
                    if (this.sortBy === 'layer') {
                        if (this.sortAsc) {
                            const aLayerTitle = a.layer.title.toUpperCase();
                            const bLayerTitle = b.layer.title.toUpperCase();
                            if (aLayerTitle < bLayerTitle) return -1;
                            if (aLayerTitle > bLayerTitle) return 1;

                            if (a.title === '' && b.title === '') {
                                return a.y - b.y;
                            } else if (!a.title) {
                                return -1;
                            } else if (!b.title) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                                const aTitle = a.title.toUpperCase();
                                const bTitle = b.title.toUpperCase();
                                if (aTitle < bTitle) return -1;
                                if (aTitle > bTitle) return 1;
                                return 0;
                            } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                                return Number.parseFloat(a.title) - Number.parseFloat(b.title);
                            } else if (Number.isNaN(Number.parseFloat(a.title))) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(b.title))) {
                                return -1;
                            }
                        }

                        const aLayerTitle = a.layer.title.toUpperCase();
                        const bLayerTitle = b.layer.title.toUpperCase();
                        if (aLayerTitle < bLayerTitle) return 1;
                        if (aLayerTitle > bLayerTitle) return -1;

                        if (a.title === '' && b.title === '') {
                            return b.y - a.y;
                        } else if (!a.title) {
                            return 1;
                        } else if (!b.title) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                            const aTitle = a.title.toUpperCase();
                            const bTitle = b.title.toUpperCase();
                            if (aTitle < bTitle) return 1;
                            if (aTitle > bTitle) return -1;
                            return 0;
                        } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                            return Number.parseFloat(b.title) - Number.parseFloat(a.title);
                        } else if (Number.isNaN(Number.parseFloat(a.title))) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(b.title))) {
                            return 1;
                        }
                    }
                    if (this.sortBy === 'cue') {
                        if (this.sortAsc) {
                            if (a.title === '' && b.title === '') {
                                return a.y - b.y;
                            } else if (!a.title) {
                                return -1;
                            } else if (!b.title) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                                const aTitle = a.title.toUpperCase();
                                const bTitle = b.title.toUpperCase();
                                if (aTitle < bTitle) return -1;
                                if (aTitle > bTitle) return 1;
                                return 0;
                            } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                                return Number.parseFloat(a.title) - Number.parseFloat(b.title);
                            } else if (Number.isNaN(Number.parseFloat(a.title))) {
                                return 1;
                            } else if (Number.isNaN(Number.parseFloat(b.title))) {
                                return -1;
                            }
                        }

                        if (a.title === '' && b.title === '') {
                            return b.y - a.y;
                        } else if (!a.title) {
                            return 1;
                        } else if (!b.title) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(a.title)) && Number.isNaN(Number.parseFloat(b.title))) {
                            const aTitle = a.title.toUpperCase();
                            const bTitle = b.title.toUpperCase();
                            if (aTitle < bTitle) return 1;
                            if (aTitle > bTitle) return -1;
                            return 0;
                        } else if (!Number.isNaN(Number.parseFloat(a.title)) && !Number.isNaN(Number.parseFloat(b.title))) {
                            return Number.parseFloat(b.title) - Number.parseFloat(a.title);
                        } else if (Number.isNaN(Number.parseFloat(a.title))) {
                            return -1;
                        } else if (Number.isNaN(Number.parseFloat(b.title))) {
                            return 1;
                        }
                    }
                }
                return a.pageNumber - b.pageNumber;
            });
        }
    },
    watch: {
        columnWidthSignature: function() {
            this.$nextTick(() => this.applyColumnWidths());
        },
        currentPage: function(newPage) {
            if (!this.splitView || !this.pageSync) return;
            this.scrollListToPage(newPage);
        },
        pageSync: function(val) {
            // Turning Page Sync on immediately aligns the list to the PDF's page
            // (PDF -> list, never the reverse — so the PDF page is preserved).
            if (val && this.splitView) {
                this.$nextTick(() => this.scrollListToPage(this.currentPage));
            }
        },
        'allCues.length': function(newLen, oldLen) {
            // A newly-created cue lands on the current PDF page but doesn't change
            // currentPage, so the page-sync watcher never fires. Re-align the list on
            // a cue add so the new cue is shown without changing pages and back.
            if (this.splitView && this.pageSync && newLen > (oldLen || 0)) {
                this.$nextTick(() => this.scrollListToPage(this.currentPage));
            }
        }
    },
    methods: {
        // Close the list-view side of split view (same as the palette toggle).
        closeSplitView: function() {
            this.$store.dispatch('setSplitView', { value: 'false' });
        },
        switchViewToPage: function(page) {
            this.$emit('switchViewToPage', page);
        },
        getUserDefined(cue) {
            const layers = this.userProjectByUidData.layers;
            if (layers && layers[cue.layer.id]) {
                if (layers[cue.layer.id].userDefined) {
                    return {
                        ...layers[cue.layer.id].userDefined
                    };
                }
            }
            return {
                user1: { label: 'User 1', value: false },
                user2: { label: 'User 2', value: false },
                user3: { label: 'User 3', value: false },
                user4: { label: 'User 4', value: false }
            };
        },
        toggleSortBy(by) {
            // Split view is locked to page -> y -> x so the list matches the PDF;
            // column-header sorting is disabled there (arrows are hidden too).
            if (this.splitView) {
                return;
            }
            if (this.sortBy === by) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortBy = by;
                this.sortAsc = true;
            }
            this.handler++;
        },
        hasUserDefined(attr) {
            return this.layerUserDefined.some(layer => layer.userDefined.hasOwnProperty(attr));
        },
        getUserDefinedValue(layer, key) {
            return layer.userDefined[key] || '';
        },
        // ONE ENTRY PER (layer, field), in the same order the rows are built.
        //
        // This used to return a Set, which silently collapsed two columns into one
        // header while every row still emitted a cell for each -- so the body had
        // more cells than the header and everything after it shifted a column.
        //
        // It needs two layers with the SAME TITLE, because layerUserDefined names a
        // column `${layer.title}: ${label}`: two differently-titled layers both with
        // a "Dept" field give "LX: Dept" and "Sound: Dept" and never collided. Two
        // layers both called "LX" give "LX: Dept" twice, and did. Duplicating a layer
        // is the obvious way to arrive there.
        //
        // Duplicate header names are the correct outcome, not a cosmetic problem:
        // they are two genuinely distinct columns that happen to share a name, and
        // the Layer Title column already tells the rows apart.
        getAllUserDefinedKeys() {
            const keys = [];
            for (const layer of this.layerUserDefined) {
                for (const key of Object.keys(layer.userDefined)) {
                    keys.push(layer.userDefined[key]);
                }
            }
            return keys;
        },
        isEditableCue: function(cue) {
            return this.editableLayers.some(layer => layer.id === cue.layer.id);
        },
        isCueSelected: function(id) {
            return this.selectedCueIds.includes(id);
        },
        toggleSelectCue: function(id, val) {
            if (val) {
                if (!this.selectedCueIds.includes(id)) {
                    this.selectedCueIds.push(id);
                }
            } else {
                this.selectedCueIds = this.selectedCueIds.filter(x => x !== id);
            }
        },
        toggleSelectAll: function(val) {
            // Scoped to currently-visible, editable rows only.
            this.selectedCueIds = val ? [...this.selectableCueIds] : [];
        },
        // Resolve selection back to raw store cue objects (allCues rewrites
        // `layer` to an object and trims titles; we need the raw x/y + layer id).
        resolveSelectedCues: function() {
            const selected = new Set(this.selectedCueIds);
            return this.cues.filter(cue => selected.has(cue.id));
        },
        openAutoNumberModal: function() {
            this.$refs.autoNumberCues.open();
        },
        onAutoNumber: function({ startNumber, interval }) {
            this.pendingAutoNumber = { startNumber, interval };
            const overwrite = this.resolveSelectedCues().filter(cue => cue.title != null && String(cue.title).trim() !== '');
            if (overwrite.length) {
                this.overwriteCount = overwrite.length;
                this.$refs.autoNumberOverwriteDialog.open();
            } else {
                this.applyAutoNumber();
            }
        },
        confirmOverwrite: function() {
            this.$refs.autoNumberOverwriteDialog.close();
            this.applyAutoNumber();
        },
        cancelOverwrite: function() {
            this.$refs.autoNumberOverwriteDialog.close();
            this.pendingAutoNumber = null;
        },
        applyAutoNumber: function() {
            if (!this.pendingAutoNumber) {
                return;
            }
            const { startNumber, interval } = this.pendingAutoNumber;
            const num = (v) => {
                const n = Number(v);
                return Number.isFinite(n) ? n : 0;
            };
            // Top-of-page-first total order: pageNumber, then y, then x.
            const ordered = this.resolveSelectedCues().slice().sort((a, b) => {
                return num(a.pageNumber) - num(b.pageNumber) || num(a.y) - num(b.y) || num(a.x) - num(b.x);
            });
            let current = startNumber;
            const updates = [];
            const prior = [];
            for (const cue of ordered) {
                prior.push({ id: cue.id, title: cue.title != null ? cue.title : '' });
                updates.push({ id: cue.id, title: String(current) });
                current = Utils.roundCueNumber(current + interval);
            }
            this.numberingInProgress = true;
            this.$store.dispatch('bulkSetCueTitles', updates).then(() => {
                this.lastNumbered = prior;
                this.resultMessage = `${updates.length} ${updates.length === 1 ? 'cue' : 'cues'} numbered.`;
                this.selectedCueIds = [];
            }).catch((e) => {
                console.error(e);
                this.lastNumbered = [];
                this.resultMessage = 'Something went wrong while numbering. No changes were saved.';
            }).then(() => {
                this.numberingInProgress = false;
                this.pendingAutoNumber = null;
                this.$refs.autoNumberResultDialog.open();
            });
        },
        undoAutoNumber: function() {
            if (!this.lastNumbered.length) {
                return;
            }
            const restore = this.lastNumbered;
            this.numberingInProgress = true;
            this.$store.dispatch('bulkSetCueTitles', restore).catch((e) => {
                console.error(e);
            }).then(() => {
                this.numberingInProgress = false;
                this.lastNumbered = [];
                this.$refs.autoNumberResultDialog.close();
            });
        },
        closeResultDialog: function() {
            this.lastNumbered = [];
            this.$refs.autoNumberResultDialog.close();
        },
        openDeleteSelected: function() {
            if (!this.selectedCueIds.length) {
                return;
            }
            this.$refs.deleteSelectedDialog.open();
        },
        cancelDeleteSelected: function() {
            this.$refs.deleteSelectedDialog.close();
        },
        confirmDeleteSelected: function() {
            this.$refs.deleteSelectedDialog.close();
            const cues = this.resolveSelectedCues();
            if (!cues.length) {
                return;
            }
            this.deletingInProgress = true;
            this.$store.dispatch('bulkDeleteCues', cues).catch((e) => {
                console.error(e);
            }).then(() => {
                this.deletingInProgress = false;
                this.selectedCueIds = [];
            });
        },
        // Every click writes the whole array (the first one seeds layers.splitViewing, which
        // ends following the script).
        changeSplitLayer(layerID, val) {
            const ids = toggledSplitLayerIDs(this.visibleSplitLayerIDs, this.layers.map(l => l.id), layerID, val);
            this.$store.dispatch('setSplitLayers', ids);
        },
        showAllSplitLayers() {
            this.$store.dispatch('setSplitLayers', this.layers.map(l => l.id));
        },
        // Close the menu on a press outside it. touchstart too, for iPad.
        onSplitLayersOutside(e) {
            if (this.splitLayersOpen && this.$refs.splitLayers && !this.$refs.splitLayers.contains(e.target)) {
                this.splitLayersOpen = false;
            }
        },
        changeVisibleListLayer(layerID, val) {
            if (val) {
                this.$store.dispatch('showListLayer', {id: layerID});
            } else {
                this.$store.dispatch('hideListLayer', {id: layerID});
            }
        },
        csvQuote(v) {
            return '"' + (v != null ? v.toString().replaceAll('"', '""') : '') + '"';
        },
        // Build one CSV row. `mode`:
        //   'whole'  — a non-parts cue: cue-level values, part columns blank.
        //   'parent' — the cue row of a parts cue: cue + globals, part columns blank.
        //   'child'  — one part of a parts cue (`part` is its partsData entry): ONLY the
        //              part columns filled; page/layer/cue/parts-count/globals all blank.
        csvRowForCue(cue, layer, part, mode) {
            const partAttrs = ['time', 'focus', 'color', 'beam', 'mark'];
            const blank = this.csvQuote('');
            const reminingData = this.availableColumns.map((attr) => {
                if (['user1', 'user2', 'user3', 'user4'].includes(attr)) {
                    return '';
                }
                const isPartCol = attr === 'parts_id' || attr === 'parts_label' || partAttrs.includes(attr);
                if (mode === 'child') {
                    // Child rows carry only the part columns.
                    if (!isPartCol) return blank;
                    if (attr === 'parts_id') return this.csvQuote(part && part.id != null ? part.id : '');
                    if (attr === 'parts_label') return this.csvQuote(part && part.label != null ? part.label : '');
                    return this.csvQuote(part && part[attr] != null ? part[attr] : '');
                }
                // Parent rows: the part columns belong to the child rows below, so they
                // stay blank here. A 'whole' row has NO child rows, so a part-NAMED
                // column must fall through to the cue's own value -- blanking it loses
                // the data outright.
                //
                // Regression 0bb01b13 (8 Jul 2026) blanked both cases alike, which
                // silently emptied Time / Focus / Color / Beam / Mark for every cue that
                // does not use parts -- i.e. most of them. Reported by a customer 10 Sept
                // as "the CSV is missing the time"; it was missing five columns.
                //
                // parts_id / parts_label are genuinely part-only and stay blank either way:
                // a cue with no parts has no part id or label to report.
                if (attr === 'parts_id' || attr === 'parts_label') return blank;
                if (mode === 'parent' && partAttrs.includes(attr)) return blank;
                if (attr === 'workNote' && Array.isArray(cue.data[attr])) {
                    return this.csvQuote(cue.data[attr].filter(item => !item.archived).map(item => item.note).join('; '));
                }
                if (attr === 'parts') {
                    return cue.data.parts != null ? this.csvQuote(cue.data.parts) : blank;
                }
                if (attr === 'followHang') {
                    const enabled = cue.data && layer.attributes && layer.attributes.followHang;
                    const type = (enabled && cue.data.followHangType) || '';
                    const text = (enabled && cue.data.followHang) || '';
                    return this.csvQuote(type + text);
                }
                const value = (cue.data && layer.attributes && (layer.attributes[attr] || attr === 'label') && cue.data[attr]) || '';
                return this.csvQuote(value);
            });
            const userDefinedData = this.layerUserDefined.flatMap((layark) => {
                return Object.entries(layark.userDefined).map(([attr]) => {
                    if (mode === 'child') return blank; // globals live on the parent row
                    let value = '-';
                    if (layark.id === layer.id && cue.data && cue.data[attr] != null) {
                        value = cue.data[attr];
                    }
                    return this.csvQuote(value != null ? value : '-');
                });
            });
            const allData = [...reminingData, ...userDefinedData];
            const filteredData = allData.filter(item => item !== '');

            // Page/Layer/Cue are echoed on every row (parent AND child) so each part
            // row keeps its cue context; only the parts-count + globals stay blank on
            // child rows.
            const pageNumber = pageForExport(this.project.pdfPageNumbers, cue.pageNumber);
            return [
                this.csvQuote(pageNumber),
                this.csvQuote(layer && layer.title ? layer.title : ''),
                this.csvQuote(cue && cue.title ? cue.title : ''),
                ...filteredData
            ].join(',');
        },
        // A structure section break as a CSV row: the name and its cue count in the
        // Section column, the page it starts on under Page Number, every other column
        // blank so the row keeps the file's column count and nothing downstream that
        // parses this CSV sees a ragged row.
        csvSectionRow(marker, columnCount, cueCount) {
            const count = Math.max(2, columnCount);
            const cellsFor = sectionCellsForExport(sidebarMarkerRowText(marker), cueCount, this.project.pdfPageNumbers, marker);
            const cells = [this.csvQuote(cellsFor.section), this.csvQuote(cellsFor.page)];
            for (let i = 2; i < count; i += 1) {
                cells.push(this.csvQuote(''));
            }
            return cells.join(',');
        },
        openEosModal() {
            this.eosModalOpen = true;
        },
        cancelEosModal() {
            if (this.eosExporting) return;
            this.eosModalOpen = false;
        },
        confirmEosExport() {
            this.exportEos();
        },
        exportEos() {
            this.eosExporting = true;
            try {
                const cues = this.allCues.filter(Boolean);
                const sections = assignSections(cues, this.sortedMarkers);
                // Sections only mean anything in document order, and Eos wants cue
                // order regardless — so export in document order whatever the list
                // is currently sorted by. With no markers this is left exactly as
                // it was, so an unflagged export is byte-identical.
                const orderedCues = this.sortedMarkers.length ? sections.orderedCues : cues;
                const sceneStarts = {};
                Object.keys(sections.headersByCueId).forEach((cueId) => {
                    // Innermost marker only. Combining "Act 1 - Prologue" named a
                    // scene that does not exist, and since the collision only
                    // happens at act boundaries it made the export inconsistent.
                    const label = innermostSceneLabel(sections.headersByCueId[cueId]);
                    if (label) sceneStarts[cueId] = label;
                });
                // Close the FINAL section only. Our sections are contiguous — every
                // cue from the first marker onward belongs to one — so each new
                // scene already closes the one before it on the console. Ending
                // every section too would put a Scene_End bar directly above the
                // next scene's bar, which is just visual noise. The last section is
                // the one nothing else closes.
                const sceneEnds = {};
                if (Object.keys(sceneStarts).length && orderedCues.length) {
                    const lastCue = orderedCues[orderedCues.length - 1];
                    if (lastCue && lastCue.id) sceneEnds[lastCue.id] = true;
                }
                const rows = buildEosRows(orderedCues, {
                    defaultTime: this.eosDefaultTime,
                    sceneStarts: sceneStarts,
                    sceneEnds: sceneEnds
                });
                const csv = toEosCsv(rows);
                // Prepend a UTF-8 BOM (Eos expects it), same as the console's own export.
                const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const rawTitle = (this.project && this.project.title) ? String(this.project.title) : '';
                const safeTitle = rawTitle.replace(/[\\/:*?"<>|]+/g, '_').trim();
                a.setAttribute('download', (safeTitle ? safeTitle + '_' : '') + 'CUELIST_FOR_EOS.csv');
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.eosExporting = false;
                    this.eosModalOpen = false;
                }, 500);
            } catch (error) {
                console.error('Error exporting for Eos:', error);
                this.eosExporting = false;
            }
        },
        exportList() {
            this.exporting = true;
            
            try {
                const reminingColumn = this.availableColumns.map((attr) => { 
                    if (!['user1', 'user2', 'user3', 'user4'].includes(attr)) { 
                        return this.cueAttributes[attr]; 
                    } 
                    return '';
                });
                // No Set here either: de-duplicating across the built-in labels and the
                // user-defined ones would re-introduce exactly the bug fixed above, and
                // the row builder never de-duplicates. The blank entries for user1-4 are
                // dropped by the filter, which is all the Set was really doing.
                const allColumns = [...reminingColumn, ...this.getAllUserDefinedKeys()];
                const filteredArray = allColumns.filter(item => item !== '');
                
                // Sections (Acts, Scenes, Songs) get their own first column, so Page
                // Number holds a page on every row: the section's first page on its
                // header row, the cue's page on a cue row (Josh, 12 Oct). The column
                // is only added when the list is showing section headers.
                const withSections = this.listRows.some(row => row.kind === 'header');
                const lead = withSections ? this.csvQuote('') + ',' : '';
                const headerCells = [...(withSections ? ['Section'] : []), 'Page Number', 'Layer Title', 'Cue Number', ...filteredArray];
                let csv = headerCells.join(',') + '\n';
                // Walk the SAME rows the list is showing, so the file mirrors the
                // screen exactly: every marker in the same order, empty sections
                // included. When headers are hidden (flag off, or a sort that hides
                // them) listRows is a plain 1:1 map of the cues and the file is
                // byte-identical to before.
                for (const row of this.listRows) {
                    if (row.kind === 'header') {
                        csv += this.csvSectionRow(row.marker, headerCells.length, row.cueCount) + '\n';
                    } else {
                        const cue = row.cue;
                        if (cue && cue.data && cue.layer) {
                            const layer = cue.layer;
                            // A multi-part cue expands to one row per part (part-level
                            // columns filled from each part; everything else repeated).
                            const partsData = (cue.data.parts && Array.isArray(cue.data.partsData) && cue.data.partsData.length)
                                ? cue.data.partsData : null;
                            if (partsData) {
                                // Parent row (cue + globals, part columns blank), then a
                                // child row per part (only the part columns filled).
                                csv += lead + this.csvRowForCue(cue, layer, null, 'parent') + '\n';
                                for (let pi = 0; pi < partsData.length; pi += 1) {
                                    csv += lead + this.csvRowForCue(cue, layer, partsData[pi], 'child') + '\n';
                                }
                            } else {
                                csv += lead + this.csvRowForCue(cue, layer, null, 'whole') + '\n';
                            }
                        }
                    }
                }

                const now = new Date();
                const hiddenElement = document.createElement('a');
                const csvData = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                hiddenElement.href = csvData;
                const filename = this.project.title + '_' + [now.getFullYear(), now.getMonth() + 1, now.getDate()].join('-') + '_' + [now.getHours(), now.getMinutes(), now.getSeconds()].join('-') + '.csv';
                hiddenElement.setAttribute('download', filename);
                document.body.appendChild(hiddenElement);
                hiddenElement.click();
                
                // Use setTimeout to allow the download to start before resetting the loading state
                setTimeout(() => {
                    this.exporting = false;
                    document.body.removeChild(hiddenElement);
                }, 500);
            } catch (error) {
                console.error('Error exporting list:', error);
                this.exporting = false;
            }
        },
        // --- Spreadsheet cell navigation + copy/paste ---
        contentEl: function() {
            const ref = this.$refs['ql-project-list-view-content'];
            return ref && ref.$el;
        },
        headerHeight: function() {
            const el = this.contentEl();
            if (!el) return 0;
            const head = el.querySelector('.ql-project-list-view-table-fixed-header');
            return head ? head.offsetHeight : 0;
        },
        onCellKeydown: function(e) {
            let from = CellNav.cellFromTarget(e.target);
            if (!from) {
                // Focus may have escaped to <body> when a virtualized row re-rendered.
                // Fall back to the tracked active cell so arrows keep navigating (and
                // never fall through to the browser's default page scroll) — but only
                // when focus landed somewhere neutral, not in another form field.
                const t = e.target;
                const el = this.contentEl();
                const neutral = t === document.body || t === document.documentElement
                    || (el && (t === el || (el.contains && el.contains(t) && !CellNav.cellFromTarget(t))));
                // In split view a neutral focus (not in a cell) means the user isn't
                // editing, so arrows should page the PDF — let the event fall through
                // to the pagination handler instead of navigating the list. In full
                // list view, keep navigating from the tracked cell so a virtualized
                // focus-drop doesn't strand the user mid-navigation.
                if (!this.splitView && this.activeCell && neutral) {
                    from = this.findCellByCoord(this.activeCell.r, this.activeCell.c)
                        || { r: this.activeCell.r, c: this.activeCell.c };
                } else {
                    return;
                }
            }
            const meta = e.metaKey || e.ctrlKey;

            if (meta && (e.key === 'c' || e.key === 'C')) {
                // Copy/paste need the real focused element; skip if we only have a
                // fallback coordinate.
                if (!from.el) return;
                // Text cells copy via the native `copy` event; selects don't fire it,
                // so handle those here with the async Clipboard API.
                if (from.kind === 'select') {
                    e.preventDefault();
                    this.writeClipboard(CellNav.serializeGrid(this.selectionGrid(from)));
                    this.flash('Copied');
                }
                return;
            }
            if (meta && (e.key === 'v' || e.key === 'V')) {
                if (from.el && from.kind === 'select') {
                    e.preventDefault();
                    if (navigator.clipboard && navigator.clipboard.readText) {
                        navigator.clipboard.readText()
                            .then((text) => {
                                const grid = CellNav.parseTSV(text);
                                if (this.rangeBounds()) this.applyRangeFill(grid);
                                else this.applyBlock(from, grid);
                            })
                            .catch(() => {});
                    }
                }
                return;
            }
            if (meta) return; // leave Cmd+Z etc. alone

            // Enter on a select moves down (text cells do this via cell-row's saveEdit,
            // which stops propagation before it reaches here).
            if (e.key === 'Enter' && from.kind === 'select') {
                e.preventDefault();
                this.clearRange();
                this.moveFocus(from, 'down');
                return;
            }

            const dirMap = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
            const dir = dirMap[e.key];
            if (!dir) return;

            if (e.shiftKey) {
                e.preventDefault();
                this.extendSelection(from, dir);
                return;
            }

            if (from.el && from.kind === 'text') {
                // Edge-aware: only leave the cell when the caret is at the boundary.
                // (No el means focus was lost and we're navigating from a coordinate —
                // there's no caret to preserve, so just move.)
                if (dir === 'left' && !CellNav.caretAtStart(from.el)) return;
                if (dir === 'right' && !CellNav.caretAtEnd(from.el)) return;
            }
            // For selects, preventDefault on every arrow so Up/Down navigate off the
            // cell instead of silently changing the option (the dropdown still opens
            // via Space / Alt+Down).
            e.preventDefault();
            this.clearRange();
            this.moveFocus(from, dir);
        },
        onCellNavigate: function(payload) {
            // Enter-moves-down from a cell (cell-row emits after blur). Resolve on the
            // next tick so the save round-trip / re-render has settled.
            this.$nextTick(() => {
                this.clearRange();
                const container = this.contentEl();
                if (!container) return;
                const from = { r: payload.r, c: payload.c };
                const target = CellNav.findTarget(container, from, payload.dir);
                if (target) {
                    this.focusTarget(target, payload.dir);
                } else if (CellNav.atWindowEdge(container, from, payload.dir)) {
                    this.moveFocus(from, payload.dir);
                } else {
                    // True last row: keep focus in place (re-focus the origin cell,
                    // which the blur cleared) rather than ejecting the user.
                    const origin = this.findCellByCoord(from.r, from.c);
                    if (origin) {
                        CellNav.focusCell(origin, false);
                        this.setActive(from.r, from.c);
                    }
                }
            });
        },
        findCellByCoord: function(r, c) {
            const container = this.contentEl();
            if (!container) return null;
            const cells = container.querySelectorAll(CellNav.CELL_SELECTOR);
            for (let i = 0; i < cells.length; i += 1) {
                const info = CellNav.cellInfo(cells[i]);
                if (info && info.r === r && info.c === c) return info;
            }
            return null;
        },
        moveFocus: function(from, dir) {
            const container = this.contentEl();
            if (!container) return;
            const target = CellNav.findTarget(container, from, dir);
            if (!target && CellNav.atWindowEdge(container, from, dir)) {
                // Held key reached the mounted-window edge but not the true list end:
                // nudge scroll to mount the next rows, then retry.
                container.scrollTop += (dir === 'down' ? 1 : -1) * (this.listRowH || 30) * 3;
                this.$nextTick(() => {
                    const retry = CellNav.findTarget(this.contentEl(), from, dir);
                    if (retry) this.focusTarget(retry, dir);
                });
                return;
            }
            if (target) this.focusTarget(target, dir);
        },
        focusTarget: function(target, dir) {
            CellNav.focusCell(target, dir === 'right');
            this.setActive(target.r, target.c);
            CellNav.ensureVisible(this.contentEl(), target.el, this.headerHeight());
            // The scroll can trigger a virtualization re-render that drops DOM focus;
            // re-assert it next tick so subsequent arrows keep originating from a cell.
            this.$nextTick(() => {
                const cell = this.findCellByCoord(target.r, target.c);
                if (cell && document.activeElement !== cell.el) {
                    CellNav.focusCell(cell, dir === 'right');
                }
            });
        },
        setActive: function(r, c) {
            this.activeCell = { r: r, c: c };
            this.paintActive();
        },
        paintActive: function() {
            const container = this.contentEl();
            if (!container) return;
            const prev = container.querySelectorAll('.ql-cell-active');
            for (let i = 0; i < prev.length; i += 1) prev[i].classList.remove('ql-cell-active');
            if (!this.activeCell) return;
            const cells = container.querySelectorAll(CellNav.CELL_SELECTOR);
            for (let i = 0; i < cells.length; i += 1) {
                const info = CellNav.cellInfo(cells[i]);
                if (info && info.r === this.activeCell.r && info.c === this.activeCell.c) {
                    cells[i].classList.add('ql-cell-active');
                }
            }
        },
        onCellFocusin: function(e) {
            const info = CellNav.cellFromTarget(e.target);
            if (info) this.setActive(info.r, info.c);
        },
        onDocMousedown: function(e) {
            if (CellNav.cellFromTarget(e.target)) {
                // A fresh click into a cell starts a new selection origin.
                this.clearRange();
                return;
            }
            // Clicked anywhere outside an editable cell — blur the focused field (so it
            // saves) and clear the active-cell highlight + range. Without this the
            // contenteditable stays focused and highlighted until another cell is clicked.
            const ae = document.activeElement;
            if (ae && typeof ae.blur === 'function'
                && ((ae.classList && ae.classList.contains('ql-cell-editable-div')) || ae.tagName === 'SELECT')) {
                ae.blur();
            }
            if (this.activeCell) {
                this.activeCell = null;
                this.paintActive();
            }
            this.clearRange();
        },
        extendSelection: function(from, dir) {
            const container = this.contentEl();
            if (!container) return;
            if (!this.rangeAnchor) this.rangeAnchor = { r: from.r, c: from.c };
            const target = CellNav.findTarget(container, from, dir);
            if (!target) return;
            this.rangeFocus = { r: target.r, c: target.c };
            CellNav.focusCell(target, false);
            CellNav.ensureVisible(container, target.el, this.headerHeight());
            this.setActive(target.r, target.c);
            this.paintRange();
        },
        clearRange: function() {
            this.rangeAnchor = null;
            this.rangeFocus = null;
            this.paintRange();
        },
        rangeBounds: function() {
            if (!this.rangeAnchor || !this.rangeFocus) return null;
            return {
                minR: Math.min(this.rangeAnchor.r, this.rangeFocus.r),
                maxR: Math.max(this.rangeAnchor.r, this.rangeFocus.r),
                minC: Math.min(this.rangeAnchor.c, this.rangeFocus.c),
                maxC: Math.max(this.rangeAnchor.c, this.rangeFocus.c)
            };
        },
        paintRange: function() {
            const container = this.contentEl();
            if (!container) return;
            const prev = container.querySelectorAll('.ql-cell-range');
            for (let i = 0; i < prev.length; i += 1) prev[i].classList.remove('ql-cell-range');
            const b = this.rangeBounds();
            if (!b) return;
            const cells = container.querySelectorAll(CellNav.CELL_SELECTOR);
            for (let i = 0; i < cells.length; i += 1) {
                const info = CellNav.cellInfo(cells[i]);
                if (info && info.r >= b.minR && info.r <= b.maxR && info.c >= b.minC && info.c <= b.maxC) {
                    cells[i].classList.add('ql-cell-range');
                }
            }
        },
        selectionGrid: function(from) {
            const b = this.rangeBounds();
            if (!b) return [[CellNav.readCellValue(from)]];
            const container = this.contentEl();
            const cells = Array.from(container.querySelectorAll(CellNav.CELL_SELECTOR))
                .map(CellNav.cellInfo)
                .filter(info => info && info.r >= b.minR && info.r <= b.maxR && info.c >= b.minC && info.c <= b.maxC);
            const rs = Array.from(new Set(cells.map(x => x.r))).sort((a, c) => a - c);
            const cs = Array.from(new Set(cells.map(x => x.c))).sort((a, c) => a - c);
            const map = {};
            cells.forEach((x) => { map[x.r + ':' + x.c] = CellNav.readCellValue(x); });
            return rs.map(r => cs.map(c => (map[r + ':' + c] !== undefined ? map[r + ':' + c] : '')));
        },
        isPasteableCell: function(info) {
            if (!info) return false;
            // Structural / composite cells are nav-only, never paste targets.
            return info.attr !== '__layer__' && info.attr !== 'parts' && info.attr !== 'workNote';
        },
        // How far down listRows a paste of `rowsNeeded` further rows will reach.
        // data-ql-r is (cueIndex * 100 + partRow), and a cue renders one row per
        // part, so the span is counted in SUB-rows, not cues.
        pasteWindowEnd: function(startInfo, rowsNeeded) {
            const rows = this.listRows;
            const startAt = this.cueIndexToRowIndex[Math.floor(startInfo.r / 100)];
            if (startAt == null) return rows.length;
            const partsOf = rw => (rw && rw.kind === 'cue'
                ? Math.max(1, Number(rw.cue && rw.cue.data && rw.cue.data.parts) || 1)
                : 0);
            // Sub-rows still below the anchor inside its own cue.
            let have = partsOf(rows[startAt]) - (startInfo.r % 100) - 1;
            let i = startAt;
            while (have < rowsNeeded && i + 1 < rows.length) {
                i += 1;
                have += partsOf(rows[i]);
            }
            return Math.min(rows.length, i + 2);
        },
        // Paste finds each target by walking the DOM (CellNav.findTarget), so it can
        // only reach rows the virtualizer has MOUNTED -- the viewport plus
        // LIST_VIRT_OVERSCAN (1000px) either side. Past that edge findTarget returns
        // null and the paste stopped, silently losing the tail. Measured 2 Sept 2026:
        // 129 rows pasted, 60 landed, and the cut-off moved between 48 and 60 rows
        // with row height because the window is bounded in PIXELS, not rows.
        //
        // So: widen the window to cover this paste, let Vue render, then run the SAME
        // walk. Deliberately NOT a data-side rewrite of target discovery -- keeping
        // the DOM walk means row order still comes from the virtualizer itself, so a
        // paste can never land on the wrong cue, and the cost is bounded by the size
        // of the paste rather than the size of the project.
        applyBlock: async function(startInfo, grid) {
            if (!grid || !grid.length) return;
            const container = this.contentEl();
            if (grid.length > 1) {
                this.pasteMountEnd = this.pasteWindowEnd(startInfo, grid.length - 1);
                await this.$nextTick();
            }
            const edits = [];
            let rowStart = startInfo;
            // Re-render can replace the anchor node; re-resolve it by coordinate if
            // the one we were handed is no longer in the tree.
            if (container && rowStart.el && !container.contains(rowStart.el)) {
                const again = container.querySelector(`[data-ql-r="${startInfo.r}"][data-ql-c="${startInfo.c}"]`);
                if (again) rowStart = CellNav.cellInfo(again) || rowStart;
            }
            let truncated = false;
            for (let i = 0; i < grid.length; i += 1) {
                let cell = rowStart;
                for (let j = 0; j < grid[i].length; j += 1) {
                    if (!cell) { truncated = true; break; }
                    const value = grid[i][j];
                    if (this.isPasteableCell(cell)) {
                        if (cell.kind === 'select') {
                            const opts = Array.from(cell.el.options);
                            if (opts.some(o => o.text === value || o.value === value)) {
                                edits.push({ cueId: cell.cueId, attr: cell.attr, part: cell.part, value });
                            }
                        } else {
                            cell.el.innerText = value; // immediate visual feedback
                            this.growColumnForCell(cell.el); // paste doesn't fire `input`
                            edits.push({ cueId: cell.cueId, attr: cell.attr, part: cell.part, value });
                        }
                    }
                    cell = CellNav.findTarget(container, cell, 'right');
                }
                if (i < grid.length - 1) {
                    const down = CellNav.findTarget(container, rowStart, 'down');
                    if (!down) { truncated = true; break; }
                    rowStart = down;
                }
            }
            if (edits.length) this.$store.dispatch('bulkUpdateCueCells', edits);
            if (truncated) {
                // Kept as the backstop, not deleted with the cause: a paste longer
                // than the list itself still has nowhere to go.
                console.warn('[list-view] paste truncated at the mounted-row window edge; scroll and paste again to continue');
                this.flash('Pasted (partial — scroll to continue)');
            } else {
                this.flash('Pasted');
            }
            // Give the window back to the scroll position, but only after the
            // store-driven re-render, so pasted values are painted from data before
            // those rows unmount.
            this.$nextTick(() => { this.pasteMountEnd = null; });
        },
        // Fill the active Shift+Arrow selection with the clipboard grid, tiling it
        // (a single copied value fills every selected cell; a block repeats to cover
        // the range). Mirrors selectionGrid's row/col ordering.
        applyRangeFill: function(grid) {
            if (!grid || !grid.length) return;
            const container = this.contentEl();
            const b = this.rangeBounds();
            if (!container || !b) return;
            const cells = Array.from(container.querySelectorAll(CellNav.CELL_SELECTOR))
                .map(CellNav.cellInfo)
                .filter(info => info && info.r >= b.minR && info.r <= b.maxR && info.c >= b.minC && info.c <= b.maxC);
            const rs = Array.from(new Set(cells.map(x => x.r))).sort((a, c) => a - c);
            const cs = Array.from(new Set(cells.map(x => x.c))).sort((a, c) => a - c);
            const rowIdx = {};
            rs.forEach((r, i) => { rowIdx[r] = i; });
            const colIdx = {};
            cs.forEach((c, j) => { colIdx[c] = j; });
            const edits = [];
            cells.forEach((cell) => {
                if (!this.isPasteableCell(cell)) return;
                const gRow = grid[rowIdx[cell.r] % grid.length];
                const value = gRow[colIdx[cell.c] % gRow.length];
                if (value === undefined) return;
                if (cell.kind === 'select') {
                    const opts = Array.from(cell.el.options);
                    if (opts.some(o => o.text === value || o.value === value)) {
                        edits.push({ cueId: cell.cueId, attr: cell.attr, part: cell.part, value });
                    }
                } else {
                    cell.el.innerText = value;
                    this.growColumnForCell(cell.el); // paste doesn't fire `input`
                    edits.push({ cueId: cell.cueId, attr: cell.attr, part: cell.part, value });
                }
            });
            if (edits.length) this.$store.dispatch('bulkUpdateCueCells', edits);
            const rowsInRange = b.maxR - b.minR;
            if (rs.length && (rs[rs.length - 1] - rs[0]) < rowsInRange) {
                // Some selected rows scrolled out of the mounted window and were not filled.
                this.flash('Pasted (partial — scroll to fill the rest)');
            } else {
                this.flash('Pasted');
            }
        },
        onCellCopy: function(e) {
            const from = CellNav.cellFromTarget(e.target);
            if (!from) return;
            if (CellNav.hasTextSelection()) return; // let native sub-text copy run
            e.preventDefault();
            e.clipboardData.setData('text/plain', CellNav.serializeGrid(this.selectionGrid(from)));
            this.flash('Copied');
        },
        onCellPaste: function(e) {
            const from = CellNav.cellFromTarget(e.target);
            if (!from) return;
            e.preventDefault();
            const cd = e.clipboardData || window.clipboardData;
            const text = cd ? cd.getData('text/plain') : '';
            const grid = CellNav.parseTSV(text);
            // A Shift+Arrow selection fills the whole range (tiling); otherwise the
            // block lands anchored at the focused cell.
            if (this.rangeBounds()) {
                this.applyRangeFill(grid);
            } else {
                this.applyBlock(from, grid);
            }
        },
        writeClipboard: function(text) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
            } else {
                this.fallbackCopy(text);
            }
        },
        fallbackCopy: function(text) {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } catch (err) { /* noop */ }
            document.body.removeChild(ta);
        },
        flash: function(msg) {
            this.flashMsg = msg;
            if (this.flashTimer) clearTimeout(this.flashTimer);
            this.flashTimer = setTimeout(() => { this.flashMsg = ''; }, 1200);
        },
        // --- Stable column widths ---
        // Column widths are pinned to the widest value across ALL cues (measured
        // off-screen), then table-layout:fixed holds them. Without this the table
        // auto-layout recomputes widths from only the mounted rows, so columns jump
        // as you scroll. Recomputed when the columns or cue count change.
        activeWorkNoteText: function(workNote) {
            if (typeof workNote === 'string') return workNote;
            if (Array.isArray(workNote)) {
                const active = workNote.filter(n => n && !n.archived);
                return active.length ? active[0].note : '';
            }
            return '';
        },
        pageLabelFor: function(cue) {
            const list = Array.isArray(this.project.pdfPageNumbers) ? this.project.pdfPageNumbers : [];
            const idx = (cue.pageNumber || 0) - 1;
            const entry = idx >= 0 && idx < list.length ? list[idx] : null;
            if (entry && entry.name) return entry.name;
            return cue.pageNumber != null ? String(cue.pageNumber) : '';
        },
        columnValues: function(cues, attr) {
            const vals = [];
            const partsAttrs = ['time', 'focus', 'color', 'beam', 'mark'];
            for (const cue of cues) {
                const d = cue.data || {};
                if (attr === 'followHang') {
                    vals.push((d.followHangType || '') + (d.followHang || ''));
                } else if (attr === 'workNote') {
                    vals.push(this.activeWorkNoteText(d.workNote));
                } else if (attr === 'parts_id' || attr === 'parts_label') {
                    const key = attr === 'parts_id' ? 'id' : 'label';
                    if (Array.isArray(d.partsData)) d.partsData.forEach(p => vals.push(p ? p[key] : ''));
                } else if (partsAttrs.indexOf(attr) !== -1) {
                    if (d.parts && Array.isArray(d.partsData)) d.partsData.forEach(p => vals.push(p ? p[attr] : ''));
                    else vals.push(d[attr]);
                } else {
                    vals.push(d[attr]);
                }
            }
            return vals;
        },
        // Per-column min/max caps. Parts columns are narrow by nature and should
        // NOT reserve regular-label width, so they get their own tighter maxes.
        columnCaps: function(attr) {
            if (attr === 'parts_id') return { min: 24, max: 60 };
            if (attr === 'parts_label') return { min: 40, max: 140 };
            if (attr === 'followHang') return { min: 90, max: 400 };
            if (attr === 'workNote') return { min: 60, max: 400 };
            if (attr === 'cue') return { min: 40, max: 200 };
            return { min: 24, max: 400 };
        },
        // Per-column tweak to the measured default width (1 = no change). Label and
        // Description read fine a bit narrower than their widest value.
        columnWidthScale: function(attr) {
            if (attr === 'label') return 0.8;
            if (attr === 'description') return 0.9;
            return 1;
        },
        applyColumnWidths: function() {
            const el = this.contentEl();
            if (!el) return;
            const table = el.querySelector('.ql-project-list-view-table');
            const headRow = table && table.querySelector('thead tr');
            if (!headRow) return;
            // Must sample a CUE cell: `tbody td` would also match a structure
            // section header, whose font is bolder/larger, and every column in
            // the table would then be measured against the wrong font.
            const sample = table.querySelector('tbody[data-cue-id] td');
            const font = sample ? window.getComputedStyle(sample).font : '';
            if (!this.measureCanvas) this.measureCanvas = document.createElement('canvas');
            const ctx = this.measureCanvas.getContext('2d');
            ctx.font = font && font.trim() ? font : '11px Arial, sans-serif';
            const PAD = 16;
            const cues = this.allCues.filter(Boolean);
            const widthFor = (texts, headerLabel, min, max) => {
                let widest = ctx.measureText(headerLabel || '').width + 12; // room for sort caret
                for (let i = 0; i < texts.length; i += 1) {
                    const t = texts[i] == null ? '' : String(texts[i]);
                    const w = ctx.measureText(t).width;
                    if (w > widest) widest = w;
                }
                return Math.max(min, Math.min(max, Math.ceil(widest) + PAD));
            };
            // Text columns are pinned by fixing the width of their inner content block
            // (see renderColWidthCss). Widths live in colWidthMap (key = attr name, or
            // '__cue__' for the cue-number column) so a single column can grow live on
            // typing without a full recompute.
            const map = {};
            const ths = Array.from(headRow.children);
            ths.forEach((th, index) => {
                const cls = th.className || '';
                let w = null;
                if (cls.indexOf('ql-cell-select') !== -1) {
                    w = 48;
                } else if (index === 1) {
                    w = 28; // delete icon
                } else if (index === 3) {
                    w = 8; // layer colour swatch
                } else {
                    const m = cls.match(/ql-cell-attr-([A-Za-z0-9_]+)/);
                    if (m) {
                        const attr = m[1];
                        const caps = this.columnCaps(attr);
                        if (attr === 'workNote' && this.notesListLinked) {
                            let widest = 0;
                            if (this.notesListAnyLiveWorkNote) {
                                this.columnValues(cues, attr).forEach((t) => {
                                    widest = Math.max(widest, ctx.measureText(t == null ? '' : String(t)).width);
                                });
                            }
                            w = notesListColumnWidth(widest, this.notesListAnyLiveWorkNote);
                        } else {
                            w = widthFor(this.columnValues(cues, attr), this.attributes[attr] || attr, caps.min, caps.max);
                            w = Math.max(caps.min, Math.round(w * this.columnWidthScale(attr)));
                        }
                        // followHang/workNote are composite cells (a select/button beside
                        // the text), so they're left unpinned.
                        if (attr !== 'followHang' && attr !== 'workNote') {
                            map[attr] = w;
                        }
                    } else {
                        const label = (th.textContent || '').replace(/arrow_drop_(up|down)/g, '').trim();
                        if (/^page/i.test(label)) {
                            w = widthFor(cues.map(c => this.pageLabelFor(c)), 'Page', 40, 120);
                        } else if (/^layer/i.test(label)) {
                            w = widthFor(cues.map(c => (c.layer ? c.layer.title : '')), 'Layer', 40, 200);
                        } else if (/^cue/i.test(label)) {
                            w = widthFor(cues.map(c => c.title), 'Cue', 40, 200);
                            // Cue-number column has no ql-cell-attr class; keyed as __cue__.
                            map['__cue__'] = w;
                        }
                    }
                }
                if (w != null) th.style.width = w + 'px';
            });
            this.colWidthMap = map;
            this.renderColWidthCss();
        },
        // Configured 2d context for measuring cell text (same font as the cells).
        measureCtx: function() {
            const el = this.contentEl();
            const table = el && el.querySelector('.ql-project-list-view-table');
            // Cue cells only — see applyColumnWidths.
            const sample = table && table.querySelector('tbody[data-cue-id] td');
            const font = sample ? window.getComputedStyle(sample).font : '';
            if (!this.measureCanvas) this.measureCanvas = document.createElement('canvas');
            const ctx = this.measureCanvas.getContext('2d');
            ctx.font = font && font.trim() ? font : '11px Arial, sans-serif';
            return ctx;
        },
        // Emit the pinned-width stylesheet from colWidthMap. Pinning the inner content
        // block's width (a value the browser honors regardless of table-layout) keeps
        // every cell in a column the same width, so the column doesn't resize on scroll.
        renderColWidthCss: function() {
            const el = this.contentEl();
            const table = el && el.querySelector('.ql-project-list-view-table');
            if (!table || !this.colWidthMap) return;
            const rules = [];
            Object.keys(this.colWidthMap).forEach((key) => {
                const w = this.colWidthMap[key];
                const sel = key === '__cue__'
                    ? '.ql-project-list-view .ql-cell-field-title .ql-cell-editable-div'
                    : '.ql-project-list-view .ql-cell-attr-' + key + ' .ql-cell-editable-div';
                rules.push(sel + '{width:' + w + 'px;max-width:' + w + 'px;}');
            });
            if (!this.colStyleEl) {
                this.colStyleEl = document.createElement('style');
                this.colStyleEl.setAttribute('data-ql-listview-colwidths', '');
                document.head.appendChild(this.colStyleEl);
            }
            this.colStyleEl.textContent = rules.join('\n');
            table.style.tableLayout = 'fixed';
        },
        // Grow a column live as the user types, up to its max cap (grow-only — never
        // shrink mid-session, which would reintroduce the scroll jitter). Refresh /
        // structural recompute re-fits from stored data as before.
        onCellInput: function(e) {
            this.growColumnForCell(e.target);
        },
        growColumnForCell: function(el) {
            if (!el || !this.colWidthMap || !el.classList) return;
            if (!el.classList.contains('ql-cell-editable-div')) return;
            const td = el.closest && el.closest('td');
            if (!td) return;
            let key = null;
            if ((td.className || '').indexOf('ql-cell-field-title') !== -1) {
                key = '__cue__';
            } else {
                const m = (td.className || '').match(/ql-cell-attr-([A-Za-z0-9_]+)/);
                if (m) key = m[1];
            }
            if (!key || key === 'followHang' || key === 'workNote') return;
            const caps = this.columnCaps(key === '__cue__' ? 'cue' : key);
            const ctx = this.measureCtx();
            const needed = Math.max(caps.min, Math.min(caps.max, Math.ceil(ctx.measureText(el.innerText || '').width) + 16));
            if (needed > (this.colWidthMap[key] || 0)) {
                this.colWidthMap[key] = needed;
                this.renderColWidthCss();
            }
        },
        // First index i in [0, n] with offs[i] > value (binary search over the
        // monotonic cumulative-offset array). Used to map a scrollTop to a cue index
        // under variable row heights.
        upperBoundOffset: function(offs, value) {
            let lo = 0;
            let hi = offs.length - 1;
            while (lo < hi) {
                const mid = Math.floor((lo + hi) / 2);
                if (offs[mid] <= value) {
                    lo = mid + 1;
                } else {
                    hi = mid;
                }
            }
            return lo;
        },
        // --- Stage A: list-view virtualization viewport tracking ---
        onListScroll: function() {
            if (this.listScrollRaf) return;
            this.listScrollRaf = requestAnimationFrame(() => {
                this.listScrollRaf = null;
                this.updateListViewport();
            });
        },
        updateListViewport: function() {
            const ref = this.$refs['ql-project-list-view-content'];
            const el = ref && ref.$el;
            if (!el) return;
            this.listVTop = el.scrollTop;
            this.listVHeight = el.clientHeight;
            // Calibrate the single-row height estimate off a CUE row only. A
            // structure section header also matches `tbody tr` but is a fixed 28px,
            // and letting it set listRowH poisons the height estimate for every
            // not-yet-measured cue (scroll flashing on long lists).
            const row = el.querySelector('.ql-project-list-view-table > tbody[data-cue-id] tr');
            if (row && row.offsetHeight > 0) this.listRowH = row.offsetHeight;
            // Structure headers are a fixed height, but honour what actually
            // rendered so browser zoom keeps rowOffsets exact.
            const hdr = el.querySelector('.ql-project-list-view-table > tbody[data-ql-structure]');
            if (hdr && hdr.offsetHeight > 0 && hdr.offsetHeight !== this.structureRowH) {
                this.structureRowH = hdr.offsetHeight;
            }
            // Rows that remount on scroll lose the imperative classes — repaint.
            if (this.rangeAnchor) this.paintRange();
            if (this.activeCell) this.paintActive();
            // Record the true height of newly-rendered rows so rowOffsets becomes
            // exact for everything scrolled through (removes the estimate drift that
            // makes part-cue-dense regions flash on fast scroll). After the window
            // re-renders, so the just-entered rows are in the DOM.
            this.$nextTick(this.measureRenderedRows);
        },
        // Cache the measured height of each rendered cue row (keyed by id|parts, so
        // a parts change re-measures). Only measures rows not already cached, so the
        // per-scroll cost is a handful of offsetHeight reads (the rows just entered).
        measureRenderedRows: function() {
            const el = this.contentEl();
            if (!el) return;
            const bodies = el.querySelectorAll('.ql-project-list-view-table > tbody[data-cue-id]');
            Array.prototype.forEach.call(bodies, (tb) => {
                const key = `${tb.getAttribute('data-cue-id')}|${tb.getAttribute('data-cue-parts')}`;
                if (this.measuredHeights[key]) return; // already measured for this parts count
                const h = tb.offsetHeight;
                if (h > 0) this.$set(this.measuredHeights, key, h);
            });
        },
        // Page sync is uni-directional: PDF -> list only. Changing the PDF page
        // scrolls the list to that page's first row. Scrolling the list never
        // moves the PDF (users jump the PDF by clicking a page number, which
        // emits switchViewToPage). The Page Sync toggle turns this on/off.
        scrollListToPage: function(page) {
            const el = this.contentEl();
            if (!el) return;
            const idx = this.allCues.findIndex(c => c.pageNumber === page);
            if (idx < 0) return; // page has no visible cues — leave the list where it is
            // Align that page's first cue to the TOP of the list (just below the
            // sticky header). rowOffsets is the exact cumulative offset under
            // variable-height (multi-part) rows, and shares scrollTop's origin, so
            // this lands the row precisely at the top instead of the flat
            // idx*rowH estimate that drifted through part-cue-dense regions.
            //
            // rowOffsets is indexed by ROW, and idx is a CUE index: the two spaces
            // diverge as soon as a section header is rendered, so cross over
            // explicitly. Scrolling to a cue that has a header above it should show
            // that header, so step back over any headers immediately preceding it.
            let rowIdx = this.cueIndexToRowIndex[idx];
            if (rowIdx === undefined) rowIdx = idx;
            while (rowIdx > 0 && this.listRows[rowIdx - 1] && this.listRows[rowIdx - 1].kind === 'header') {
                rowIdx -= 1;
            }
            el.scrollTop = this.rowOffsets[rowIdx] || 0;
        }
    },
    components: {
        'project-list-view-cue-row': ProjectListViewCueRow,
        'project-list-view-structure-header-row': StructureHeaderRow,
        'auto-number-cues': AutoNumberCues
    },
    mounted: function() {
        document.addEventListener('mousedown', this.onSplitLayersOutside);
        document.addEventListener('touchstart', this.onSplitLayersOutside, { passive: true });
        if (this.$refs['ql-project-list-view-content']) {
            const elm = this.$refs['ql-project-list-view-content'].$el;
            elm.addEventListener('scroll', this.onListScroll, { passive: true });
            // Spreadsheet nav + copy/paste, delegated on the scroll container.
            // keydown listens at the document (capture) so arrows keep working even
            // if a virtualized re-render drops focus to <body> — a container-scoped
            // listener would stop firing and the browser would scroll the page.
            document.addEventListener('keydown', this.onCellKeydown, true);
            elm.addEventListener('copy', this.onCellCopy);
            elm.addEventListener('paste', this.onCellPaste);
            elm.addEventListener('focusin', this.onCellFocusin);
            elm.addEventListener('input', this.onCellInput);
            // Document-level so clicking ANYWHERE outside a cell (toolbar, empty space,
            // off the table) defocuses the field.
            document.addEventListener('mousedown', this.onDocMousedown);
        }
        // Measure the real viewport + row height before paint so the first render
        // is already windowed (never mounts all rows).
        this.$nextTick(() => {
            this.updateListViewport();
            this.applyColumnWidths();
            // On entering split with Page Sync on, align the list to the current PDF page.
            if (this.splitView && this.pageSync) {
                this.scrollListToPage(this.currentPage);
            }
        });
    },
    beforeDestroy: function() {
        document.removeEventListener('mousedown', this.onSplitLayersOutside);
        document.removeEventListener('touchstart', this.onSplitLayersOutside, { passive: true });
        if (this.$refs['ql-project-list-view-content']) {
            const elm = this.$refs['ql-project-list-view-content'].$el;
            elm.removeEventListener('scroll', this.onListScroll);
            document.removeEventListener('keydown', this.onCellKeydown, true);
            elm.removeEventListener('copy', this.onCellCopy);
            elm.removeEventListener('paste', this.onCellPaste);
            elm.removeEventListener('focusin', this.onCellFocusin);
            elm.removeEventListener('input', this.onCellInput);
            document.removeEventListener('mousedown', this.onDocMousedown);
        }
        if (this.flashTimer) clearTimeout(this.flashTimer);
        if (this.colStyleEl && this.colStyleEl.parentNode) {
            this.colStyleEl.parentNode.removeChild(this.colStyleEl);
            this.colStyleEl = null;
        }
    }
};

export default ProjectListView;
</script>

<style lang="scss">
.ql-project-list-view {

    background: #fff;
    z-index: 19;
    position: fixed;
    left: 0;
    top: 46px;
    width: 100%;
    padding: 1rem;
    -ms-flex-direction: column;
    flex-direction: column;
    height: 100%;
    max-height: calc(100vh - 47px);
    overflow-y: auto;

    .ql-structure-sort-hint {
        width: 100%;
        margin: -4px 0 8px;
        font-size: 11px;
        font-style: italic;
        opacity: 0.7;
    }

    .ql-project-list-view-toolbar {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;

        .list-view-visible-layers {
            display: flex;
            align-items: center;
        }

        .list-view-export {
            display: flex;
            align-items: center;
        }

        .list-view-split-bar {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;

            .ql-split-close {
                margin-left: auto;
                border: none;
                background: transparent;
                font-size: 22px;
                line-height: 1;
                color: #888;
                cursor: pointer;
                padding: 0 4px;
            }
            .ql-split-close:hover {
                color: #222;
            }

            // Split view's own layer picker (Josh, 21 Oct 2026).
            .ql-split-layers {
                position: relative;
            }
            .ql-split-layers-btn {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font: inherit;
                font-weight: 600;
                color: inherit;
                background: transparent;
                border: 1px solid #b9c3cc;
                border-radius: 3px;
                padding: 3px 8px;
                cursor: pointer;
                white-space: nowrap;
            }
            .ql-split-layers-btn:hover,
            .ql-split-layers-btn.is-open {
                background: rgba(13, 39, 68, 0.06);
            }
            .ql-split-layers-btn.is-filtered {
                border-color: #0d2744;
            }
            .ql-split-layers-caret {
                width: 0;
                height: 0;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-top: 5px solid currentColor;
            }
            .ql-split-layers-menu {
                position: absolute;
                top: calc(100% + 4px);
                left: 0;
                z-index: 50;
                min-width: 180px;
                max-height: 60vh;
                overflow-y: auto;
                background: #fff;
                border: 1px solid #ccc;
                border-radius: 4px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
                padding: 6px 0;
            }
            .ql-split-layer-option {
                display: flex;
                align-items: center;
                gap: 6px;
                margin: 0;
                padding: 5px 12px;
                cursor: pointer;
                white-space: nowrap;

                input[type="checkbox"] {
                    accent-color: #0d2744;
                    cursor: pointer;
                    margin: 0;
                }
            }
            .ql-split-layer-option:hover {
                background: rgba(13, 39, 68, 0.06);
            }
            .ql-split-layer-swatch {
                width: 10px;
                height: 10px;
                border-radius: 2px;
                flex: 0 0 10px;
            }
            .ql-split-layers-foot {
                border-top: 1px solid #e3e3e3;
                margin-top: 4px;
                padding: 6px 12px 2px;

                button {
                    border: none;
                    background: transparent;
                    color: #164e7d;
                    font: inherit;
                    padding: 0;
                    cursor: pointer;
                }
            }

            .ql-split-page-sync {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font-weight: 600;
                cursor: pointer;
                white-space: nowrap;

                input[type="checkbox"] {
                    accent-color: #0d2744; // match the app's md-checkbox (theme primary)
                    cursor: pointer;
                }
            }
        }
    }

    .ql-split-empty {
        padding: 24px 16px;
        color: #888;
        text-align: center;
    }

    .ql-project-list-view-content {
        width: 100%;
        height: 100%;
        overflow: auto;
        display: block;
    }

    .ql-list-view-flash {
        position: fixed;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(22, 78, 125, 0.92);
        color: #fff;
        padding: 6px 14px;
        border-radius: 4px;
        font-size: 13px;
        z-index: 40;
        pointer-events: none;
    }

    .ql-project-list-view-table {

        border: 0 none;

        border-collapse: collapse;

        thead th, tbody td {
            border: solid 1px #ccc;
            font-size: 11px;
            padding: 1px 4px;
            cursor: default;
            white-space: nowrap;
        }

        thead th {
            background: #164e7d;
            color: #fff;
            // Native sticky header (replaces the old JS translateY hack). Also lets
            // columns freeze on the X axis, which a transformed ancestor would break.
            position: sticky;
            top: 0;
            z-index: 4;
            // Sticky cells drop their collapsed borders, so redraw the column
            // divider (and header bottom edge) with box-shadow.
            box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.22), inset 0 -1px 0 rgba(255, 255, 255, 0.22);
        }

        tbody td {
            max-width: 400px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
        tbody td div:focus {
            &, * {
                overflow: visible;
                text-overflow: initial;
            }
        }
        // Active-cell affordance — without a visible focus ring the whole
        // arrow-nav / copy-paste feature is undiscoverable (11px cells, a
        // collapsed caret is invisible).
        tbody td .ql-cell-editable-div:focus,
        tbody td select:focus,
        tbody td .ql-cell-active {
            outline: none;
            box-shadow: inset 0 0 0 2px #164e7d;
            background: #eaf2fb;
        }
        // Shift+Arrow range selection highlight.
        tbody td.ql-cell-range,
        tbody td .ql-cell-range,
        tbody td.ql-cell-range .ql-cell-editable-div,
        .ql-cell-range {
            background: #d3e5fb;
        }

        .ql-cell-text-left {
            text-align: left;
        }
        .ql-cell-text-center {
            text-align: center;
        }
        .ql-cell-field-pageNumber {
            cursor: pointer;
        }
        .ql-cell-attr-pageNumber {
            cursor: pointer;
        }
        .ql-cell-layer-color {
            width: 7px;
        }
        .ql-cell-sortable {
            cursor: pointer;
        }
        .ql-cell-editable-div {
            cursor: pointer;
            width: 100%;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }
        .ql-cell-empty {
            border: 0px none;
            background: #fff;
        }
        .ql-cell-attr-delete {
            cursor: pointer;
            .md-icon {
                font-size: 20px;
            }
        }
        .ql-cell-select {
            // header cell has a white background (ql-cell-empty), so the "All"
            // label needs dark text — the default thead th color is #fff.
            color: #164e7d;
            // vue-material's md-checkbox ships 16px top+bottom margin (32px),
            // which was inflating every row's height. Strip it to slim rows.
            .md-checkbox {
                margin: 0;
            }
            .md-checkbox-label {
                color: #164e7d;
                font-size: 14px;
            }
        }
        .ql-cell-attr-label {
            border-left-width: 3px;
        }
        .ql-cell-attr-followHang {
            // room for the F/H dropdown + a few chars of follow/hang text so
            // Safari doesn't clip the value (the dropdown steals inline width).
            min-width: 90px;
        }
        .ql-cell-attr-workNote {
            max-width: unset;
            & > .ql-cell-attr-workNote-content {
                display: flex;
                align-items: center;
            }
            .btn-toggle-workNote {
                min-width: 24px;
                min-height: 24px;
                width: auto;
                height: auto;
                margin: 0 8px 0 0;
                i {
                    color: #5ecfdf !important;
                }
            }
        }
    }
}

    .md-theme-show-mode {
        .ql-project-list-view {
            background: #424242;

            .ql-cell-editable {
                select {
                    background: #424242;
                    color: #fff;
                }
            }
            .ql-cell-empty {
                background: #424242;
            }
            .ql-cell-select {
                color: #fff;
                .md-checkbox-label {
                    color: #fff;
                }
            }
            .ql-project-list-view-table {
                .even_color {
                    background-color: #676767;

                    td{
                        color: #fff;
                    }
                    .ql-cell-editable {
                        select {
                            color: #fff;
                        }
                    }
                }
                // Dark-mode focus/active/range highlights: the light-blue used in
                // light mode leaves white text unreadable, so use a dark-blue fill
                // with a bright border and explicit white text.
                tbody td .ql-cell-editable-div:focus,
                tbody td select:focus,
                tbody td .ql-cell-active {
                    background: #21507f;
                    box-shadow: inset 0 0 0 2px #5ecfdf;
                    color: #fff;
                }
                tbody td.ql-cell-range,
                tbody td .ql-cell-range,
                .ql-cell-range {
                    background: #33608a;
                    color: #fff;
                }
            }

        }
        .gray, .black {
            color: #fff;
        }
    }
/* END DARK THEME CODE */

/* Export-for-Eos modal (native, no vue-material) */
.ql-eos-modal-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 60;
}
.ql-eos-modal {
    background: #fff;
    color: #333;
    border-radius: 6px;
    padding: 20px 22px;
    width: 320px;
    max-width: 90vw;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
}
.ql-eos-modal .ql-eos-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 6px;
}
.ql-eos-modal .ql-eos-modal-desc {
    font-size: 13px;
    color: #666;
    margin: 0 0 14px;
}
.ql-eos-modal .ql-eos-modal-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
}
.ql-eos-modal .ql-eos-modal-input {
    display: block;
    width: 100%;
    margin-top: 4px;
    padding: 8px;
    font-size: 15px;
    border: 1px solid #bbb;
    border-radius: 4px;
    box-sizing: border-box;
}
.ql-eos-modal .ql-eos-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
}
.ql-eos-modal .ql-eos-btn {
    border: 0;
    border-radius: 2px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    cursor: pointer;
    background: #e0e0e0;
    color: #333;
}
.ql-eos-modal .ql-eos-btn-primary {
    background: #164e7d;
    color: #fff;
}
.ql-eos-modal .ql-eos-btn-primary:disabled {
    opacity: 0.6;
    cursor: default;
}

// --- Split view: freeze the identifying leading columns during horizontal scroll
// so the user always sees which cue a row is (delete / page / colour / layer / cue).
// Fixed widths make the sticky-left offsets deterministic (cumulative widths).
.ql-project-list-view-content.ql-split-list .ql-project-list-view-table {
    td.ql-cell-empty:not(.ql-cell-select),
    thead th:nth-child(2)  { width: 34px  !important; min-width: 34px;  max-width: 34px; }
    .ql-cell-field-pageNumber,
    thead th:nth-child(3)  { width: 46px  !important; min-width: 46px;  max-width: 46px; }
    .ql-cell-layer-color,
    thead th:nth-child(4)  { width: 8px   !important; min-width: 8px;   max-width: 8px; }
    .ql-cell-field-layer,
    thead th:nth-child(5)  { width: 92px  !important; min-width: 92px;  max-width: 92px; }
    .ql-cell-field-title,
    thead th:nth-child(6)  { width: 60px  !important; min-width: 60px;  max-width: 60px; }

    // Freeze to the left edge. Offsets = cumulative widths of the columns to the left.
    td.ql-cell-empty:not(.ql-cell-select) { position: sticky; left: 0;   z-index: 3; background: #fff; }
    .ql-cell-field-pageNumber           { position: sticky; left: 34px;  z-index: 3; background: #fff; }
    .ql-cell-layer-color                { position: sticky; left: 80px;  z-index: 3; }
    .ql-cell-field-layer                { position: sticky; left: 88px;  z-index: 3; background: #fff; }
    .ql-cell-field-title                { position: sticky; left: 180px; z-index: 3; background: #fff; }

    // Frozen header cells: sticky on BOTH axes (top from thead th rule, left here),
    // opaque and stacked ABOVE the scrolling headers so headers that scroll behind
    // the frozen block are fully occluded (not peeking out on the left).
    thead th:nth-child(2) { position: sticky; left: 0;     z-index: 7; background: #164e7d; }
    thead th:nth-child(3) { position: sticky; left: 34px;  z-index: 7; background: #164e7d; }
    thead th:nth-child(4) { position: sticky; left: 80px;  z-index: 7; background: #164e7d; }
    thead th:nth-child(5) { position: sticky; left: 88px;  z-index: 7; background: #164e7d; }
    thead th:nth-child(6) { position: sticky; left: 180px; z-index: 7; background: #164e7d; }

    // Divider between the frozen block and the scrolling columns, on the last frozen
    // column (Cue). Box-shadow because sticky cells drop collapsed borders.
    .ql-cell-field-title,
    thead th:nth-child(6) {
        box-shadow: inset -1px 0 0 #999, 3px 0 5px -2px rgba(0, 0, 0, 0.28);
    }

    // Hide the sort-direction arrows in the split list: they clip inside the narrow
    // fixed-width frozen columns (e.g. the Page arrow). Header click still sorts.
    thead th .md-icon {
        display: none;
    }

    // Arrows are hidden, so highlight the header the list is currently sorted by.
    // !important beats the frozen-header occlusion background on the sortable columns.
    thead th.ql-sort-active {
        background: #2c6aa6 !important;
    }
}

// Dark mode: the split view layer picker.
.md-theme-show-mode .ql-project-list-view .ql-project-list-view-toolbar .list-view-split-bar {
    .ql-split-layers-btn { border-color: #777; }
    .ql-split-layers-btn:hover,
    .ql-split-layers-btn.is-open { background: rgba(255, 255, 255, 0.08); }
    .ql-split-layers-btn.is-filtered { border-color: #73d5e3; }
    .ql-split-layers-menu { background: #303030; border-color: #555; color: #eee; }
    .ql-split-layer-option:hover { background: rgba(255, 255, 255, 0.08); }
    .ql-split-layers-foot { border-top-color: #555; }
    .ql-split-layers-foot button { color: #73d5e3; }
}

// Dark mode: frozen body cells need the dark row background, not white.
.md-theme-show-mode .ql-project-list-view-content.ql-split-list .ql-project-list-view-table {
    td.ql-cell-empty:not(.ql-cell-select),
    .ql-cell-field-pageNumber,
    .ql-cell-field-layer,
    .ql-cell-field-title { background: #424242; }
}
</style>


// WEBPACK FOOTER //
// list-view.vue?38eb5249